// ===========================================================================
// Shopify Cart API Wrapper
// ===========================================================================
const cartAPI = {
  async get() {
    console.log('[Cart API] Firing: get()');
    const response = await fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    const data = await response.json();
    console.log('[Cart API] Response from get():', data);
    return data;
  },
  async add(formData) {
    console.log('[Cart API] Firing: add() with formData:', formData);
    const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
    const data = await response.json();
    console.log('[Cart API] Response from add():', data);
    return data;
  },
  async update(formData) {
    console.log('[Cart API] Firing: update() with formData:', formData);
    const response = await fetch('/cart/update.js', { method: 'POST', body: formData });
    const data = await response.json();
    console.log('[Cart API] Response from update():', data);
    return data;
  },
  async change(line, quantity) {
    console.log(`[Cart API] Firing: change() for line ${line} to quantity ${quantity}`);
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    const data = await response.json();
    console.log('[Cart API] Response from change():', data);
    return data;
  }
};

// ===========================================================================
// Helper Functions (Outer Scope to reduce complexity)
// ===========================================================================

/**
 * Formats a number with specific delimiters.
 * Always returns a string.
 */
function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
  if (Number.isNaN(number) || number == null) return '0.00';

  const fixedNumber = (number / 100.0).toFixed(precision);
  const parts = fixedNumber.split('.');
  const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  
  return dollars + (parts[1] ? decimal + parts[1] : '');
}

/**
 * Formats cents into a price string based on Shopify settings.
 */
function formatMoney(cents, format) {
  let value = '';
  let processedCents = cents;
  
  if (typeof processedCents === 'string') {
    processedCents = processedCents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || (globalThis.ShopifyTheme && globalThis.ShopifyTheme.moneyFormat) || '₹{{amount}}');

  const match = formatString.match(placeholderRegex);
  const formatType = match ? match[1] : 'amount';

  switch (formatType) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(processedCents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(processedCents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(processedCents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(processedCents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

// ===========================================================================
// Module Initializers
// ===========================================================================

function initLibraries() {
  if (typeof AOS !== 'undefined') {
    console.log('Library Found: Initializing AOS animations.');
    AOS.init({ duration: 800, once: true });
  } else {
    console.warn('Library Not Found: AOS is not defined.');
  }

  if (typeof lucide !== 'undefined') {
    console.log('Library Found: Creating Lucide icons.');
    lucide.createIcons();
  } else {
    console.warn('Library Not Found: lucide is not defined.');
  }
}

function initMenuDrawer() {
  console.log('[Menu Drawer] Initializing...');
  const openBtn = document.getElementById('open-menu-drawer');
  const drawer = document.getElementById('menu-drawer');
  const overlay = document.getElementById('menu-drawer-overlay');
  const content = document.getElementById('menu-drawer-content');
  const closeBtn = document.getElementById('close-menu-drawer');

  if (!drawer) return;

  const openMenuDrawer = () => {
    drawer.classList.remove('invisible');
    overlay.classList.remove('opacity-0', 'invisible');
    content.classList.remove('-translate-x-full');
    document.body.classList.add('no-scroll');
  };

  const closeMenuDrawer = () => {
    overlay.classList.add('opacity-0');
    content.classList.add('-translate-x-full');
    setTimeout(() => {
      drawer.classList.add('invisible');
      overlay.classList.add('invisible');
    }, 300);
    document.body.classList.remove('no-scroll');
  };

  if (openBtn) openBtn.addEventListener('click', openMenuDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeMenuDrawer);
  if (overlay) overlay.addEventListener('click', closeMenuDrawer);
}

function initCartDrawer() {
  console.log('[Cart Drawer] Initializing...');
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-drawer-overlay');
  const content = document.getElementById('cart-drawer-content');
  const closeBtn = document.getElementById('close-cart-drawer');
  const openBtn = document.getElementById('open-cart-drawer');

  if (!drawer) return;

  const openCartDrawer = () => {
    drawer.classList.remove('invisible');
    overlay.classList.remove('opacity-0', 'invisible');
    content.classList.remove('translate-x-full');
    document.body.classList.add('no-scroll');
  };

  const closeCartDrawer = () => {
    overlay.classList.add('opacity-0');
    content.classList.add('translate-x-full');
    setTimeout(() => {
      drawer.classList.add('invisible');
      overlay.classList.add('invisible');
    }, 300);
    document.body.classList.remove('no-scroll');
  };

  // Expose open function to global scope for AJAX cart triggers
  globalThis.openCartDrawer = openCartDrawer;

  if (closeBtn) closeBtn.addEventListener('click', closeCartDrawer);
  if (overlay) overlay.addEventListener('click', closeCartDrawer);

  if (openBtn) {
    openBtn.addEventListener('click', (event) => {
      event.preventDefault();
      openCartDrawer();
      if (typeof globalThis.renderNewCartDrawer === 'function') {
        globalThis.renderNewCartDrawer();
      } else {
        console.error('[Cart Drawer] Error: window.renderNewCartDrawer is not defined.');
      }
    });
  }
}

function initAjaxCart() {
  const forms = document.querySelectorAll('form[action="/cart/add"]');
  console.log(`[AJAX Cart] Found ${forms.length} "Add to Cart" forms.`);

  forms.forEach((form, index) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitBtn = form.querySelector('button[type="submit"]');
      const btnText = submitBtn.querySelector('.cart-text');
      
      if (submitBtn) submitBtn.disabled = true;
      if (btnText) btnText.textContent = 'Adding...';

      const formData = new FormData(form);
      const response = await cartAPI.add(formData);

      if (response.status === 422) {
        alert(response.description);
        if (btnText) btnText.textContent = 'Add to Cart';
        if (submitBtn) submitBtn.disabled = false;
      } else {
        if (typeof globalThis.renderNewCartDrawer === 'function') {
          globalThis.renderNewCartDrawer();
        }
        
        // Use the globally exposed function from initCartDrawer
        if (typeof globalThis.openCartDrawer === 'function') {
          globalThis.openCartDrawer();
        }

        if (btnText) btnText.textContent = 'Added!';
        setTimeout(() => {
          if (btnText) btnText.textContent = 'Add to Cart';
          if (submitBtn) submitBtn.disabled = false;
        }, 1500);
      }
    });
  });
}

function initQuantitySelectors() {
  const selectors = document.querySelectorAll('.quantity-selector');
  selectors.forEach(selector => {
    const input = selector.querySelector('input[type="number"]');
    const btnDec = selector.querySelector('[data-action="decrement"]');
    const btnInc = selector.querySelector('[data-action="increment"]');

    if (!input || !btnDec || !btnInc) return;

    btnDec.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > 1) input.value = val - 1;
    });

    btnInc.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      input.value = val + 1;
    });
  });
}

function updateMainImage(mediaId, mainImage, thumbnailLinks, productJson) {
  const newImageSrc = productJson.media.find(media => media.id == mediaId)?.src;
  if (newImageSrc && mainImage) {
    mainImage.src = newImageSrc.replace(/(\.[^.]*)$/, '_1024x$1');
  }
  
  thumbnailLinks.forEach(link => {
    const img = link.querySelector('img');
    if (link.dataset.imageId == mediaId) {
      img.classList.add('border-brand-purple');
      img.classList.remove('border-transparent');
    } else {
      img.classList.remove('border-brand-purple');
      img.classList.add('border-transparent');
    }
  });
}

function initProductPage() {
  const productForm = document.querySelector('#product-form');
  // Check if productJson is available in global scope
  if (!productForm || typeof productJson === 'undefined') return;

  console.log('[Product Form] Initializing variant logic.');
  
  const variantSelectors = productForm.querySelectorAll('select');
  const priceElement = document.getElementById('product-price');
  const addToCartBtn = productForm.querySelector('button[type="submit"]');
  const addToCartText = document.getElementById('add-to-cart-text');
  const mainImage = document.getElementById('main-product-image');
  const hiddenVariantInput = productForm.querySelector('input[name="id"]');
  const thumbnailLinks = document.querySelectorAll('.thumbnail-link');

  const onVariantChange = () => {
    const selectedOptions = Array.from(variantSelectors).map(s => s.value);
    const selectedVariant = productJson.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!selectedVariant) {
      if (addToCartBtn) addToCartBtn.disabled = true;
      if (addToCartText) addToCartText.textContent = 'Unavailable';
      return;
    }

    hiddenVariantInput.value = selectedVariant.id;
    if (priceElement) priceElement.textContent = formatMoney(selectedVariant.price);
    
    if (selectedVariant.featured_media && mainImage) {
      updateMainImage(selectedVariant.featured_media.id, mainImage, thumbnailLinks, productJson);
    }

    if (addToCartBtn) {
      if (selectedVariant.available) {
        addToCartBtn.disabled = false;
        if (addToCartText) addToCartText.textContent = 'Add to Cart';
      } else {
        addToCartBtn.disabled = true;
        if (addToCartText) addToCartText.textContent = 'Sold Out';
      }
    }
  };

  variantSelectors.forEach(s => s.addEventListener('change', onVariantChange));
  
  thumbnailLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      updateMainImage(link.dataset.imageId, mainImage, thumbnailLinks, productJson);
    });
  });

  // Initial check
  onVariantChange();
}

function initCartPage() {
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  const updateBtn = cartForm.querySelector('button[name="update"]');
  if (updateBtn) {
    updateBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      updateBtn.textContent = 'Updating...';
      updateBtn.disabled = true;
      const formData = new FormData(cartForm);
      await cartAPI.update(formData);
      globalThis.location.reload();
    });
  }
}

// ===========================================================================
// Main Entry Point
// ===========================================================================
document.addEventListener('DOMContentLoaded', () => {
  console.log('✅ DOMContentLoaded: Initializing theme scripts.');
  
  initLibraries();
  initMenuDrawer();
  initCartDrawer();
  initAjaxCart();
  initQuantitySelectors();
  initProductPage();
  initCartPage();
});